// @flow

import React, { PureComponent } from 'react';
import type { RouterHistory } from 'react-router';
import { createFragmentContainer, graphql } from 'react-relay';
import debounce from 'lodash.debounce';
import { BrowserUtils, i18n } from 'shared/utils';
import Pill from 'shared/components/common/Pill';
import Table from 'shared/components/common/Table';
import LoadingMore from 'shared/components/common/LoadingMore';
import EmptySearchResults from 'shared/components/common/EmptySearchResults';
import EmptyListResults from 'shared/components/common/EmptyListResults';
import type { BomsTable_itemEdges as ItemEdgesFragment } from './__generated__/BomsTable_itemEdges';

type Props = {
  history: RouterHistory,
  filtersDirty: boolean,
  itemEdges: ItemEdgesFragment,
};

class BomsTable extends PureComponent<Props> {
  render() {
    const { history, itemEdges, filtersDirty } = this.props;
    const edges = itemEdges ? itemEdges.filter(Boolean).map(edge => edge.node) : [];

    if (!edges.length) {
      return filtersDirty ? (
        <EmptySearchResults />
      ) : (
        <EmptyListResults
          graphic="items"
          message={i18n.t(
            'Bills of materials will appear here when items have components assigned.',
          )}
        />
      );
    }

    return (
      <Table
        data={edges}
        onRowClick={rowData => history.push(`/item/${rowData.id}/components`)}
        columns={[
          {
            Header: i18n.t('BOM #'),
            accessor: 'bomNumber',
            width: 120,
            Cell: ({ value }: *) => <Pill>{value || '—'}</Pill>,
          },
          {
            Header: i18n.t('Inventory #'),
            accessor: 'itemNumber',
            width: 120,
            Cell: ({ value }: *) => <Pill>{value}</Pill>,
          },
          {
            Header: i18n.t('Part #'),
            accessor: 'partNumber',
            width: 120,
          },
          {
            Header: i18n.t('Name'),
            accessor: 'name',
          },
          {
            Header: i18n.t('Description'),
            accessor: 'description',
          },
          {
            Header: i18n.t('Components'),
            accessor: 'bomComponentCount',
            width: 110,
            Cell: ({ value }: *) =>
              value != null ? i18n.t('{{quantity, number}}', { quantity: value }) : '—',
          },
        ]}
      />
    );
  }
}

export default createFragmentContainer(BomsTable, {
  itemEdges: graphql`
    fragment BomsTable_itemEdges on ItemEdge @relay(plural: true) {
      node {
        id
        bomNumber
        itemNumber
        partNumber
        name
        description
        bomComponentCount
      }
    }
  `,
});
